import { AnyExercise, isCuratedExerciseById, isCustomExerciseById } from '@cf-shared/data-model/exercise';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { GET_EXERCISE_NOT_FOUND, GetExerciseParams, GetExerciseResults } from '@cf-shared/publish-exercise-interface';

// Resolving an exercise id against the two databases that can hold one.
//
// An exercise lives in one of two places, and which one is knowable from the id:
//   - a CURATED exercise (`exercise/$…`) is in the read-only `global_common`
//     catalog, read through ExerciseDbService;
//   - a CUSTOM exercise (`exercise/+UDE…`) is one the user made themselves, in
//     their own database, read through UserDb.
// They are separate database wrappers and they fail independently, which is why
// this order matters and why the two failures below mean different things.
//
// This module exists because the same curated-then-custom resolution used to be
// written out by hand at four call sites, so any change to what a lookup MEANS
// had to be made four times and audited at every caller (df-0d4b8f148612).
// `locateExerciseByIdAsync` below adds a third place, the `getExercise` cloud
// function, for the two pages that can be handed a share-link id (df-c13ee8cb9ad4).
//
// Structural interfaces rather than the Angular service classes: it keeps this
// module out of the services' import graph — `user-db.service.ts` already
// imports runtime values from `exercise-db.service.ts`, so a resolver ON that
// service would close a module cycle — and it lets the unit test hand in fakes.
// `exercise-lineage.ts` does the same for the same reasons.
//
// The curated lookup is the THROWING read: `undefined` means only "not in the catalog", and
// every read that failed is thrown. The quieter `getGlobalExerciseByIdAsync` answers a failed
// read as `undefined`, which here would read as a missing exercise (df-7b992e109846).
export interface ResolveCuratedLookup {
  getGlobalExerciseByIdOrThrowAsync(id: string, watcher: unknown): Promise<AnyExercise | undefined>;
}
export interface ResolveCustomLookup {
  getCustomExerciseByIdAsync(id: string): Promise<AnyExercise>;
}

/**
 * The exercise this id names, wherever it lives — the curated catalog first,
 * then the user's own exercises.
 *
 * The curated read answers `undefined` for a custom id without reading anything,
 * so the fallback below is what actually resolves a custom exercise.
 *
 * TWO FAILURES, AND THEY MEAN DIFFERENT THINGS. Both travel to the caller, which
 * is what every one of the existing call sites already relied on:
 *
 *  - **The custom exercise is not there.** `getCustomExerciseByIdAsync` THROWS a
 *    PouchDB not-found error; it never answers `undefined`. So "no such exercise"
 *    arrives here as a throw, and it keeps travelling. Callers that want to show
 *    their own message catch it (exercise-stats does); the rest let it travel.
 *  - **The curated read failed.** Whatever stopped it — a database that could not
 *    be read at all (`DatabaseUnavailableError`), or a pass-through fetch that timed
 *    out — is thrown rather than answered as `undefined`, and this function does not
 *    catch it. So a failed catalog read never falls through to the user database.
 *    That is deliberate: the fallback is for "not in the catalog", and treating "we
 *    could not look" as that would tell a user their exercise is gone when it is
 *    merely unreachable for a moment (#4737, df-7b992e109846).
 */
export async function resolveAnyExerciseByIdAsync(
  id: string,
  curated: ResolveCuratedLookup,
  custom: ResolveCustomLookup,
  watcher: unknown,
): Promise<AnyExercise> {
  return await curated.getGlobalExerciseByIdOrThrowAsync(id, watcher) || await custom.getCustomExerciseByIdAsync(id);
}

/** Which of the three places an exercise id was found in. */
export enum ExerciseLocation {
  /** the read-only `global_common` catalog */
  Curated = 'curated',
  /** the user's own database */
  Own = 'own',
  /** the `getExercise` cloud function, which reads the shared `global_exercises` database */
  Cloud = 'cloud',
}

export interface LocatedExercise {
  from: ExerciseLocation;
  exercise: AnyExercise;
}

export interface ResolveCloudLookup {
  getExerciseAsync(params: GetExerciseParams): Promise<GetExerciseResults>;
}

/** The `getExercise` cloud function answered, but not with an exercise or a not-found. */
export class GetExerciseFailedError extends Error {
  readonly exerciseId: string;
  readonly serverError: string | undefined;

  constructor(exerciseId: string, serverError: string | undefined) {
    super(`getExercise ${exerciseId}: ${serverError ?? 'answer carried neither an exercise nor an error'}`);
    this.name = 'GetExerciseFailedError';
    this.exerciseId = exerciseId;
    this.serverError = serverError;
  }
}

/**
 * The exercise a `getExercise` answer carries, or `undefined` when the server said it is not
 * there. The function never throws on the server: it answers `{ error }` both for a genuine
 * miss and for a read of `global_exercises` that failed. Only the not-found answer means the
 * exercise is gone. Anything else is thrown, so a server that could not look is never shown
 * to the user as "no longer available" (df-f5a9c683e16d).
 */
export function exerciseFromGetExerciseResult(exerciseId: string, result: GetExerciseResults): AnyExercise | undefined {
  if (result?.exercise) return result.exercise;
  if (result?.error === GET_EXERCISE_NOT_FOUND) return undefined;
  throw new GetExerciseFailedError(exerciseId, result?.error);
}

/**
 * Where this id's exercise lives, and the exercise itself — or `undefined` when none of
 * the places that could hold it has it. It answers WHERE as well as WHAT because the two
 * callers draw different conclusions from the answer: view-exercise decides whether to
 * offer Import, edit-exercise only wants a name (df-c13ee8cb9ad4).
 *
 * The id's prefix picks the first place to look:
 *  - a CURATED id (`exercise/$…`) reads only the `global_common` catalog. A miss there is
 *    FINAL. The cloud function reads only `global_exercises`, and the server accepts
 *    only `exercise/+UDE-…` ids into it, so asking it about a curated id could only ever
 *    answer "not found".
 *  - a CUSTOM id (`exercise/+UDE…`) reads the user's own database first. That read THROWS
 *    a PouchDB not-found error on a missing document, and only that error falls through
 *    to the cloud: an exercise from a share link can look local by its prefix and still
 *    live only in `global_exercises` until replication has landed it.
 *  - any other id goes straight to the cloud.
 *
 * Every other error travels to the caller: `DatabaseUnavailableError`, a curated read that
 * failed any other way, a cloud call that failed, and a cloud answer that is neither an
 * exercise nor a not-found (`GetExerciseFailedError`). A database we could not read is not
 * an exercise that is missing (#4737, df-7b992e109846).
 */
export async function locateExerciseByIdAsync(
  id: string,
  curated: ResolveCuratedLookup,
  custom: ResolveCustomLookup,
  cloud: ResolveCloudLookup,
  watcher: unknown,
): Promise<LocatedExercise | undefined> {
  if (isCuratedExerciseById(id)) {
    const exercise = await curated.getGlobalExerciseByIdOrThrowAsync(id, watcher);
    return exercise ? { from: ExerciseLocation.Curated, exercise } : undefined;
  }
  if (isCustomExerciseById(id)) {
    try {
      return { from: ExerciseLocation.Own, exercise: await custom.getCustomExerciseByIdAsync(id) };
    } catch (err) {
      if (!isPouchNotFound(err)) throw err;
      // not in the user's own database - fall through to the cloud
    }
  }
  const exercise = exerciseFromGetExerciseResult(id, await cloud.getExerciseAsync({ exerciseId: id }));
  return exercise ? { from: ExerciseLocation.Cloud, exercise } : undefined;
}
